import Firebase from 'firebase';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import FirebaseService from 'services/FirebaseService';
import {
  USER_HAS_BEEN_KICKED_OR_BANNED,
  UPDATE_WEB_PRESENCE_CHAT_STATUS,
} from 'services/MessagingService/EventNames';
import messagingService from 'services/MessagingService/MessagingService';
import ModalService from 'services/ModalService';
import StreamingStore from 'stores/StreamingStore';

import firebaseNode from '../firebase/firebaseNode';
import { FIREBASE_AUTH_STATUS } from '../firebase/firebaseStructure';
import { MESSAGE_CONFIG } from '../enum/messages';


const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);
const CONNECTION_RETRY_DELAY = 200;
const LIMIT_OF_RETRY = 5;

class FirebaseRoomService {
  constructor() {
    this.messagingService = messagingService();
    this.doFirebaseAuthClojureInstance = this.doFirebaseAuthClojure();
    this.api = {
      // eslint-disable-next-line max-len
      GUEST_FIREBASE_ROOM_REQUEST_URL: `${WEBCHAT_HOST}requestAccess?r={rnd}&roomname={roomName}&chat_history_limit={chatHistoryLimit}`,
      // eslint-disable-next-line max-len
      AUTHENTICATED_FIREBASE_ROOM_REQUEST_URL: `${WEBCHAT_HOST}requestAccess?r={rnd}&roomname={roomName}&chat_history_limit={chatHistoryLimit}&accessHash={accessHash}&username={username}&pass={pass}`,
      WEBCHAT_POST_MESSAGE_MAIN_CHAT: `${WEBCHAT_HOST}writeMessage`,
    };
    this.watcher = null;
  }

  initRoom = (roomName, usernameArg, accessHash, pass = '') => {
    let username = usernameArg;
    const multiplier = 100000000;
    if (username) {
      username = username.toLowerCase();
    }
    const rnd = Math.floor((Math.random() * multiplier) + 1);
    const isGuest = (!username || username === '') && (!accessHash || accessHash === '');
    let firebaseRoomRequestUrl = undefined;
    if (isGuest) {
      firebaseRoomRequestUrl = this.api.GUEST_FIREBASE_ROOM_REQUEST_URL;
    } else {
      firebaseRoomRequestUrl = this.api.AUTHENTICATED_FIREBASE_ROOM_REQUEST_URL;
    }
    let chatHistoryLimit = isGuest ? MESSAGE_CONFIG.MAIN_CHAT.GUEST_LIMIT : MESSAGE_CONFIG.MAIN_CHAT.LOGGED_IN_LIMIT;
    chatHistoryLimit = window?.abt?.chat?.historyLimit || chatHistoryLimit;

    const parsedFirebaseRoomRequestUrl = ApiService.substitute(
      firebaseRoomRequestUrl,
      { rnd, roomName, accessHash, username, pass: encodeURIComponent(pass), chatHistoryLimit }
    );

    return ApiService.get(parsedFirebaseRoomRequestUrl);
  };

  showErrorModal() {
    StreamingStore.setBroadcastOver(true);
    ModalService.showModal(
      ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL
    );
  }


  roomAccessSuccess(roomName, username, authData, isGuest, callBackFnc) {
    const { shard, token, guestNodeName } = authData;

    if (isGuest) {
      return this.doFirebaseAuth(shard, token, roomName, username, callBackFnc, guestNodeName);
    }

    return this.doFirebaseAuth(shard, token, roomName, username, callBackFnc);
  }

  doFirebaseAuth = (cam4FirebaseHost, tokenChat, roomName, username, callBackFnc, guestNodeName) => {
    FirebaseService.initFirebase(cam4FirebaseHost);
    const baseConnectionRef = FirebaseService.FirebaseMain.child(firebaseNode.CHAT_ROOMS);
    const serverTimeOffsetRef = FirebaseService.FirebaseMain.child(firebaseNode.SERVER_TIME_OFFSET);
    this.doFirebaseAuthClojureInstance(
      baseConnectionRef,
      tokenChat,
      callBackFnc, [
        roomName,
        username,
        guestNodeName,
        serverTimeOffsetRef,
        cam4FirebaseHost,
      ]
    );
    Firebase.goOnline();
  };

  doFirebaseAuthClojure() {
    let numberOfRetries = 0;

    return (baseConnectionRef, tokenChat, callBackFnc, callBackFncProps) => {
      baseConnectionRef.authWithCustomToken(tokenChat, (error) => {
        if (numberOfRetries > LIMIT_OF_RETRY) {
          numberOfRetries = 0;
          callBackFnc(error, ...callBackFncProps);
        } else {
          numberOfRetries += 1;
          setTimeout(() => {
            this.doFirebaseAuthClojureInstance(baseConnectionRef, tokenChat, callBackFnc, callBackFncProps);
          }, CONNECTION_RETRY_DELAY);
        }
      });
    };
  }

  doFirebaseAuthForExternalStreamSync = (cam4FirebaseHost, tokenChat) => {
    FirebaseService.initFirebase(cam4FirebaseHost, 'Notifications');
    const baseConnectionRef = FirebaseService.FirebaseMain;

    baseConnectionRef.authWithCustomToken(tokenChat, (error) => {
      if (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });
  };

  initWebPresence = (roomName, username) => {
    if (this.watcher) {
      return;
    }

    this.watcher = roomName + username;

    const connectedRef = FirebaseService.FirebaseMain.child(firebaseNode.CONNECTED_REF);

    connectedRef.once('value', (snapshot) => {
      const isConnected = !!snapshot.val();
      if (!isConnected) {
        return;
      }

      const pushNode = FirebaseService.FirebaseMain.child('p').child(roomName).child(username)
        .push();

      pushNode.onDisconnect().remove(() => {
        this.watcher = null;
      });
      // [TODO] Callback on remove to remove watcher value;
      pushNode.set({ t: Firebase.ServerValue.TIMESTAMP });
    });

    connectedRef.on('value', (snapshot) => {
      const isConnected = !!snapshot.val();
      this.messagingService.emit(UPDATE_WEB_PRESENCE_CHAT_STATUS, isConnected);
    });
  };

  removeWebPresence = () => {
    const connectedRef = FirebaseService.FirebaseMain && FirebaseService.FirebaseMain.child(firebaseNode.CONNECTED_REF);
    const baseConnection = FirebaseService.FirebaseMain && FirebaseService.FirebaseMain.child(firebaseNode.CHAT_ROOMS);
    if (connectedRef) {
      connectedRef.off();
    }
    Firebase.goOffline();
    if (baseConnection) {
      baseConnection.unauth();
    }
  };

  removeRosterListener = (roomName) => {
    const rosterLightRef = FirebaseService.FirebaseMain && FirebaseService.FirebaseMain.child(`r/${roomName}`);
    if (rosterLightRef) {
      rosterLightRef.off();
    }
  };

  guestsPresence = (roomName, guestNodeName) => {
    const connectedRef = FirebaseService.FirebaseMain.child(firebaseNode.CONNECTED_REF);
    connectedRef.on('value', (snap) => {
      if (snap.val() === true) {
        const newChatlessGuestConnectionRef = FirebaseService.FirebaseMain.child(
          `${firebaseNode.CHAT_ROOMS}/${roomName}/${firebaseNode.GUESTS_PRESENCE}/${guestNodeName}`
        );
        newChatlessGuestConnectionRef.onDisconnect().set(null);
        newChatlessGuestConnectionRef.set({ o: 1 });
      }
    });
  };

  authDataStatusHandler = (status, isGuest) => {
    switch (status) {
      case FIREBASE_AUTH_STATUS.bannedForRoom:
        return 'status: banned from room';
      case FIREBASE_AUTH_STATUS.alreadyInTheRoom:
        return 'status: already in the room';
      case FIREBASE_AUTH_STATUS.privateRoom:
        if (isGuest) {
          // eslint-disable-next-line no-console
          console.warn('status: guest in private room');
        } else {
          // eslint-disable-next-line no-console
          console.warn('status: authenticated user in private room');
        }

        return undefined;
      case FIREBASE_AUTH_STATUS.userIsFromBlockedCountryOrRegion:
        return 'status: user is from blocked country or region';
      case FIREBASE_AUTH_STATUS.wrongPassword:
        return 'status: wrong password';
      case FIREBASE_AUTH_STATUS.success:
        // eslint-disable-next-line no-console
        console.warn('status: success');

        return undefined;
      default:
        return 'status: failed';
    }
  };

  handleRoomNotification = (snapshot, roomName, viewerUsername) => {
    const kickOrBan = snapshot.val();
    this.kbData = { kickOrBan, roomName, viewerUsername };
    this.messagingService.emit(USER_HAS_BEEN_KICKED_OR_BANNED, this.kbData);
  };

  rosterChildChanged = (roomName, callBackFnc, viewerUsername) => {
    const rosterLightRef = FirebaseService.FirebaseMain.child(`r/${roomName}`);
    rosterLightRef.on('child_changed', (snapshot) => callBackFnc(snapshot, viewerUsername));
  };

  rosterChildAdded = (roomName, callBackFnc, viewerUsername) => {
    const rosterLightRef = FirebaseService.FirebaseMain.child(`r/${roomName}`);
    rosterLightRef.on('child_added', (snapshot) => callBackFnc(snapshot, viewerUsername));
  };

  rosterChildRemoved = (roomName, callBackFnc, viewerUsername) => {
    const rosterLightRef = FirebaseService.FirebaseMain.child(`r/${roomName}`);
    rosterLightRef.on('child_removed', (snapshot) => callBackFnc(snapshot));
    if (viewerUsername) {
      const myRoomNotificationsRef = FirebaseService.FirebaseMain.child(
        `${firebaseNode.CHAT_ROOMS}/${roomName}/${firebaseNode.WEB_USER_ROOM_NOTIFICATIONS}/${viewerUsername}`
      );
      myRoomNotificationsRef.on('child_removed', (snapshot) =>
        this.handleRoomNotification(snapshot, roomName, viewerUsername)
      );
    }
  };
}

export default new FirebaseRoomService();

